import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  ReportProblemOutlined,
  Refresh,
  ArrowBack,
  ExpandMore,
  ExpandLess,
  ContentCopy,
} from "@mui/icons-material";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Production logger hook or reporting service integration
    console.error("Uncaught application error captured by ErrorBoundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
    });
    if (typeof this.props.onReset === "function") {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCopyTrace = () => {
    const errorText = `${this.state.error?.toString()}\n\nComponent Stack:\n${
      this.state.errorInfo?.componentStack || "N/A"
    }`;
    navigator.clipboard.writeText(errorText);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return typeof this.props.fallback === "function"
          ? this.props.fallback({
              error: this.state.error,
              resetErrorBoundary: this.handleReset,
            })
          : this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: this.props.isolated ? "300px" : "100vh",
            p: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "background.default" : "grey.100",
          }}
        >
          <Card
            elevation={0}
            sx={{
              maxWidth: 620,
              width: "100%",
              borderRadius: 3.5,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* Header Strip */}
            <Box
              sx={{
                height: 6,
                background: "linear-gradient(90deg, #ef4444 0%, #f97316 100%)",
              }}
            />

            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack spacing={2.5} alignItems="center" sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "error.50",
                    color: "error.main",
                    border: "1px solid",
                    borderColor: "error.200",
                  }}
                >
                  <ReportProblemOutlined sx={{ fontSize: 36 }} />
                </Avatar>

                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mb: 0.5 }}>
                    Something went wrong
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    An unexpected application error occurred. You can retry the current action or reload the screen safely.
                  </Typography>
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Refresh />}
                  onClick={this.handleReload}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3 }}
                >
                  Reload Page
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBack />}
                  onClick={this.handleReset}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                >
                  Try Again
                </Button>
              </Stack>

              {/* Collapsible Error Trace for Debugging */}
              {this.state.error && (
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => this.setState((prev) => ({ showDetails: !prev.showDetails }))}
                    endIcon={this.state.showDetails ? <ExpandLess /> : <ExpandMore />}
                    sx={{ textTransform: "none", color: "text.secondary", fontSize: "0.78rem" }}
                  >
                    {this.state.showDetails ? "Hide Technical Details" : "View Technical Details"}
                  </Button>

                  <Collapse in={this.state.showDetails}>
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
                        border: "1px solid",
                        borderColor: "divider",
                        textAlign: "left",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={this.handleCopyTrace}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        title={this.state.copied ? "Copied!" : "Copy Error Details"}
                      >
                        <ContentCopy sx={{ fontSize: 16, color: this.state.copied ? "success.main" : "text.secondary" }} />
                      </IconButton>

                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: "monospace",
                          color: "error.main",
                          fontWeight: 700,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          mb: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        {this.state.error.toString()}
                      </Typography>

                      {this.state.errorInfo?.componentStack && (
                        <Typography
                          variant="caption"
                          component="pre"
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: 180,
                            overflowY: "auto",
                            fontSize: "0.7rem",
                          }}
                        >
                          {this.state.errorInfo.componentStack}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
